/**
 * The technical trust bar — Halil's spec, verbatim in shape.
 *
 * *"Web sitemizde: Home page + Enterprise AI Solutions page. Hero sonrasında,
 * ilk sayfada veya ilk scroll'da gözükecek şekilde…"* — three rows, each a
 * category and the names in it, ending in "and many more" because the real list
 * is longer than three screens could hold and a closed list invites "why isn't
 * X on there" for every integration not named.
 *
 * **Microsoft appears twice, and that is not a duplicate to clean up.** It is an
 * application vendor (Microsoft 365, Dynamics) in one row and a model provider
 * (Copilot / Azure OpenAI) in the other — two different claims that happen to
 * share a vendor. Read literally from the email rather than deduplicated, the
 * way the customer wall's `foldName` join was read literally off the page it was
 * matching rather than cleaned up to look tidier. Since 2026-08-28 they no
 * longer share a *logo* either: the models row carries the **Microsoft AI**
 * (MAI) mark, on Kagan's *"Microsoft yerine microsoft AI logosu olsun"*. Same
 * request named the model rather than its owner in two more slots — Google
 * became **Gemini** and Alibaba became **Qwen** — which is what the rest of that
 * row already did (Anthropic and OpenAI are the exceptions, being the names
 * their marks carry).
 *
 * **`padded` marks the one file that carries its own whitespace.** The MAI mark
 * we were sent is a 474x474 canvas with a three-letter wordmark across the
 * middle of it — the drawn ink is about 42% of the file's height, so a height
 * cap that fits every other mark draws this one at less than half the size it
 * looks like it should be. It is the `albarakaturk.png` problem from
 * `content/customerWall.ts`, in the trust bar rather than on the wall.
 * `StackBadge` gives a padded file a much taller box so the *ink* lands where
 * its neighbours' ink lands; the extra canvas is transparent and costs the row
 * nothing. Set it on a file, never on a brand — if the mark is ever re-cropped,
 * the flag comes off with it.
 *
 * English only: `Translation`'s array rule means a locale supplies a section's
 * whole array or none of it, and DAIMI-style local company names are not the
 * concern here — every name in this list is already the international brand
 * name, in every language this site ships. `content/pages/tr/*.ts` therefore
 * import this same file rather than re-typing it — see the note on `STACK_TR`.
 */
export const STACK_GROUPS_EN: {
  heading: string
  items: { name: string; src: string; padded?: boolean }[]
}[] = [
  {
    heading: 'Integrated Applications',
    items: [
      { name: 'SAP', src: '/logo/sap.webp' },
      { name: 'Oracle', src: '/logo/oracle.webp' },
      { name: 'IFS', src: '/logo/ifs.webp' },
      { name: 'Microsoft', src: '/logo/microsoft.webp' },
      { name: 'Salesforce', src: '/logo/salesforce.webp' },
      { name: 'Workday', src: '/logo/workday.webp' },
      { name: 'and many more', src: '' },
    ],
  },
  {
    heading: 'Connected AI Models',
    items: [
      { name: 'Anthropic', src: '/logo/anthropic.webp' },
      { name: 'Mistral', src: '/logo/mistral.webp' },
      { name: 'OpenAI', src: '/logo/openai.webp' },
      { name: 'Gemini', src: '/logo/gemini.png' },
      { name: 'Microsoft AI', src: '/logo/microsoftai.png', padded: true },
      { name: 'DeepSeek', src: '/logo/deepseek.png' },
      { name: 'Qwen', src: '/logo/qwen.webp' },
      { name: 'and many more', src: '' },
    ],
  },
  {
    heading: 'Supported Infrastructures',
    items: [
      { name: 'AWS', src: '/logo/aws.webp' },
      { name: 'Google Cloud', src: '/logo/googlecloud.webp' },
      { name: 'MS Azure', src: '/logo/azure.webp' },
      { name: 'and many more', src: '' },
    ],
  },
]

/**
 * Turkish reads the category labels in Turkish; the brand names inside them do
 * not translate — nobody writes "Açık Kaynak Yapay Zeka" for OpenAI, the same
 * reason `tr/enterprise-ai-solutions.ts` keeps MCP, A2A, API, RPA, GRC and
 * FinOps: established technical terms, not prose.
 *
 * **"and many more" is not a brand name, and does get translated** — it is
 * filler prose that happened to live in the same array as fifteen brand names.
 * Leaving it in English read as a seam: three Turkish headings, English brand
 * names as expected, and then an English sentence fragment where the reader
 * expects Turkish to resume. Matched by empty `src`, the same test the renderer
 * uses to know an entry has no logo to resolve.
 */
export const STACK_GROUPS_TR: {
  heading: string
  items: { name: string; src: string; padded?: boolean }[]
}[] = STACK_GROUPS_EN.map((g, i) => ({
    heading: ['Entegre uygulamalar', 'Bağlı modeller', 'Desteklenen altyapılar'][i],
    items: g.items.map((item) =>
      item.name === 'and many more' ? { ...item, name: 've daha fazlası' } : item,
    ),
  }))
